import { ChangeEvent, ComponentPropsWithRef, FocusEvent, forwardRef, MouseEvent, useRef, useState, useCallback } from "react";
import InputStyle from "./css/Input.module.css";
import clsx from "clsx";
import { InputManagerFactory } from "./utils/interface/inputManager";

type InputTextType = "text" | "phone" | "email" | "name" | "id";

type InputProps = ComponentPropsWithRef<"input"> & {
    fullWidth?: boolean;
    minWidth?: number;
    width?: string | number;
    textType?: InputTextType;
    gray?: boolean;
    border?: number;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    hiddenLeftIconIfFocused?: boolean;
    hiddenRightIconIfFocused?: boolean;
    hidden?: boolean;
};

/*
const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const target = e.currentTarget;
    if (
        (type === "date" || 
         type === "datetime-local" || 
         type === "time" ||
         type === "month" ||
         type === "week") &&
        'showPicker' in target &&
        typeof target.showPicker === 'function'
    ) {
        try {
            target.showPicker();
        } catch (error) {
            console.warn('showPicker not supported:', error);
        }
    }
    onClick?.(e);
};
*/

const Input = forwardRef<HTMLInputElement, InputProps>(({
    type = "text",
    fullWidth = false,
    minWidth,
    width,
    textType = "text",
    onChange,
    onFocus,
    onBlur,
    onClick,
    border=1,
    gray,
    hiddenLeftIconIfFocused = true,
    hiddenRightIconIfFocused = false,
    leftIcon,
    rightIcon,
    style,
    hidden,
    ...props
}, ref) => {
    const [isOn, setIsOn] = useState(false);
    const internalRef = useRef<HTMLInputElement>(null);
    
    // 외부 ref와 내부 ref 모두 연결
    const setRefs = useCallback((element: HTMLInputElement | null) => {
        internalRef.current = element;
        if (typeof ref === 'function') {
            ref(element);
        } else if (ref) {
            (ref as React.MutableRefObject<HTMLInputElement | null>).current = element;
        }
    }, [ref]);
    // const inputManger = InputManagerFactory.create(type, textType);
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        // const formattedValue = inputManger.format(e.target.value);
        onChange?.(e);
    };

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
        onFocus?.(e);
        setIsOn(true);
        
        // date 관련 타입이면 포커스 시 picker 표시
        const input = e.currentTarget;
        if (
            (type === "date" || 
             type === "datetime-local" || 
             type === "time" ||
             type === "month" ||
             type === "week") &&
            'showPicker' in input &&
            typeof input.showPicker === 'function'
        ) {
            try {
                input.showPicker();
            } catch (error) {
                // showPicker not supported
            }
        }
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        onBlur?.(e);
        if(e.target.value === "") {
            setIsOn(false);
        }
    };

    const handleWrapperClick = (e: MouseEvent<HTMLDivElement>) => {
        internalRef.current?.focus();
    }

    const handleInputClick = (e: MouseEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        
        // date 관련 타입이면 picker 표시
        if (
            (type === "date" || 
             type === "datetime-local" || 
             type === "time" ||
             type === "month" ||
             type === "week") &&
            'showPicker' in input &&
            typeof input.showPicker === 'function'
        ) {
            try {
                input.showPicker();
            } catch (error) {
                // showPicker not supported
            }
        }
        
        onClick?.(e);
    }

    const adjustWidthValue = () => {
        let adj = 0;
        if(leftIcon) { adj += 24; }
        if(rightIcon) { adj += 24; }
        return adj;
    }

    return (
        <div className={clsx(
                InputStyle.wrapper,
                fullWidth && InputStyle["full-width"],
                gray && InputStyle["gray"],
            )}
            style={{
                backgroundColor: style?.backgroundColor,
                border: border ? `${border}px solid rgba(221, 221, 221, 1)` : "none",
                '--input-bg': gray ? "rgba(243, 243, 243, 1)" : style?.backgroundColor,
                display: hidden ? "none" : undefined,
                width: fullWidth ? undefined : width,
            } as React.CSSProperties}
            onClick={handleWrapperClick}
        >
            {leftIcon && (!isOn || !hiddenLeftIconIfFocused) && <div className={InputStyle.icon}>{leftIcon}</div>}
            <input
                ref={setRefs}
                className={clsx(
                    InputStyle.input,
                )}
                style={{
                    minWidth: minWidth ? `${minWidth}px` : undefined,
                    backgroundColor: gray ? "rgba(243, 243, 243, 1)" : style?.backgroundColor,
                    ...style
                }}
                type={type}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClick={handleInputClick}
                {...props}
            />
            {rightIcon && (!isOn || !hiddenRightIconIfFocused) && <div className={InputStyle.icon}>{rightIcon}</div>}
        </div>
    )
});

Input.displayName = "Input";

export default Input;
export type {
    InputTextType,
    InputProps
};
